import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Edit, Trash2, Eye, TrendingUp, TrendingDown, Coins as CoinsIcon } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Coins = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Add modal state for Grant Coins
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);

  // New state for user search in Grant Coins modal
  const [userQuery, setUserQuery] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Fetch coin transactions on component mount
  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await adminService.getCoinTransactions();
      
      if (data && data.transactions && Array.isArray(data.transactions)) {
        setTransactions(data.transactions);
        setError(null);
        console.log('Transactions loaded:', data.transactions.length, 'Total:', data.total);
      } else if (Array.isArray(data)) {
        setTransactions(data);
        setError(null);
        console.log('Transactions loaded (direct array):', data.length);
      } else {
        console.error('Unexpected data format:', data);
        setTransactions([]);
        setError('Invalid data format received from server');
        toast.error('Invalid data format received');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
      setError(err.message);
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockTransactions = [
    {
      id: 1,
      user_name: 'John Doe',
      user_email: 'john@example.com',
      amount: 100,
      type: 'add',
      description: 'Welcome bonus',
      created_at: '2024-01-20T10:00:00.000Z',
      credit_debit: 'credit',
    },
    {
      id: 2,
      user_name: 'Jane Smith',
      user_email: 'jane@example.com',
      amount: -50,
      type: 'deduct',
      description: 'Campaign creation fee',
      created_at: '2024-01-19T15:30:00.000Z',
      credit_debit: 'debit',
    },
  ];

  // Use real data if available, otherwise fallback to mock
  const displayTransactions = transactions.length > 0 ? transactions : mockTransactions;
  
  const filteredTransactions = displayTransactions.filter(transaction => {
    const matchesSearch = (
      (transaction.user_name && transaction.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (transaction.user_email && transaction.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (transaction.description && transaction.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const matchesType = selectedType === 'all' || transaction.credit_debit === selectedType;
    return matchesSearch && matchesType;
  });

  const getTypeBadge = (type) => {
    const typeConfig = {
      credit: 'bg-green-100 text-green-800',
      debit: 'bg-red-100 text-red-800',
      add: 'bg-green-100 text-green-800',
      deduct: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeConfig[type] || 'bg-gray-100 text-gray-800'}`}>
        {type === 'credit' || type === 'add' ? 'Credit' : 'Debit'}
      </span>
    );
  };

  const formatAmount = (amount) => {
    const numAmount = Number(amount);
    return (
      <span className={`font-medium ${numAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {numAmount >= 0 ? '+' : ''}{numAmount} coins
      </span>
    );
  };

  const getTotalStats = () => {
    const totalCredits = displayTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const totalDebits = displayTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
    
    const netBalance = totalCredits - totalDebits;
    
    return { totalCredits, totalDebits, netBalance };
  };

  const stats = getTotalStats();

  // Modal handlers
  const handleView = (transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };

  // Handler for Grant Coins button
  const handleGrantCoinsClick = () => {
    setShowGrantModal(true);
    setGrantEmail('');
    setGrantAmount('');
  };

  // User search effect
  useEffect(() => {
    if (!userQuery) {
      setUserDetails(null);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        // Use backend search for name/email/id
        const data = await adminService.getUsers({ search: userQuery });
        const users = data.users || [];
        // Try exact match by ID, then email, then name
        let user = null;
        if (/^\d+$/.test(userQuery)) {
          user = users.find(u => String(u.id) === userQuery);
        }
        if (!user && userQuery.includes('@')) {
          user = users.find(u => u.email === userQuery);
        }
        if (!user) {
          user = users.find(u => u.name && u.name.toLowerCase().includes(userQuery.toLowerCase()));
        }
        setUserDetails(user || null);
      } catch {
        setUserDetails(null);
      }
      setUserSearchLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [userQuery]);

  const handleGrantCoinsSubmit = async (e) => {
    e.preventDefault();
    if (!userDetails) return;
    setGrantLoading(true);
    try {
      await adminService.grantCoins(userDetails.id, grantAmount, "Granted by admin");
      toast.success('Coins granted!');
      setShowGrantModal(false);
      fetchTransactions();
    } catch (err) {
      toast.error('Failed to grant coins');
    } finally {
      setGrantLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coins Management</h1>
          <p className="text-gray-600">
            {error ? 'Showing demo data (API connection failed)' : 'Manage platform coins and transactions'}
          </p>
        </div>
        <button className="btn-primary flex items-center" onClick={handleGrantCoinsClick}>
          <Plus className="h-4 w-4 mr-2" />
          Grant Coins
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Credits</p>
              <p className="text-2xl font-bold text-green-600">{stats.totalCredits.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Debits</p>
              <p className="text-2xl font-bold text-red-600">{stats.totalDebits.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <CoinsIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Net Balance</p>
              <p className={`text-2xl font-bold ${stats.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.netBalance >= 0 ? '+' : ''}{stats.netBalance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transactions...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-gray-600 mb-4">Showing demo data below. Click retry to attempt fetching real data again.</p>
            <button onClick={fetchTransactions} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user name, email, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="input-field"
            >
              <option value="all">All Types</option>
              <option value="credit">Credits</option>
              <option value="debit">Debits</option>
            </select>
            <button className="btn-secondary flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      {!loading && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {transaction.user_name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {transaction.user_name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {transaction.user_email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatAmount(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(transaction.credit_debit)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {transaction.description || 'No description'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-primary-600 hover:text-primary-900" onClick={() => handleView(transaction)}>
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-blue-600 hover:text-blue-900">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button className="btn-secondary">Previous</button>
              <button className="btn-secondary">Next</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredTransactions.length}</span> of{' '}
                  <span className="font-medium">{filteredTransactions.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button className="btn-secondary rounded-l-md">Previous</button>
                  <button className="btn-secondary rounded-r-md">Next</button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {showModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Transaction Details</h2>
            <div className="space-y-2">
              <p><strong>User:</strong> {selectedTransaction.user_name} ({selectedTransaction.user_email})</p>
              <p><strong>Amount:</strong> {formatAmount(selectedTransaction.amount)}</p>
              <p><strong>Type:</strong> {getTypeBadge(selectedTransaction.credit_debit)}</p>
              <p><strong>Description:</strong> {selectedTransaction.description}</p>
              <p><strong>Date:</strong> {selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : 'N/A'}</p>
            </div>
            <button className="btn-primary mt-6 w-full" onClick={closeModal}>Close</button>
          </div>
        </div>
      )}

      {/* Grant Coins Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowGrantModal(false)}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Grant Coins</h2>
            <form onSubmit={handleGrantCoinsSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">User (Name, Email, or ID)</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={userQuery}
                  onChange={e => setUserQuery(e.target.value)}
                  required
                  placeholder="Enter name, email, or user ID"
                />
                {userSearchLoading && <p className="text-xs text-gray-500">Searching...</p>}
                {userDetails && (
                  <div className="mt-2 p-2 border rounded bg-gray-50">
                    <div><strong>Name:</strong> {userDetails.name}</div>
                    <div><strong>Email:</strong> {userDetails.email}</div>
                    <div><strong>ID:</strong> {userDetails.id}</div>
                  </div>
                )}
                {!userSearchLoading && userQuery && !userDetails && (
                  <div className="text-xs text-red-500 mt-1">No user found.</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={grantAmount}
                  onChange={e => setGrantAmount(e.target.value)}
                  required
                  min={1}
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={grantLoading || !userDetails}
              >
                {grantLoading ? 'Granting...' : 'Grant Coins'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredTransactions.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <CoinsIcon className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedType !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No coin transactions have been recorded yet.'
            }
          </p>
          <button className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Grant Coins
          </button>
        </div>
      )}
    </div>
  );
};

export default Coins;
